import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supportService } from "../../services/api";

// Async thunks
export const fetchSupports = createAsyncThunk(
  "supports/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await supportService.getAllSupports();
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSupportById = createAsyncThunk(
  "supports/fetchById",
  async (supportId, { rejectWithValue }) => {
    try {
      const response = await supportService.getSupportById(supportId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createSupport = createAsyncThunk(
  "supports/create",
  async (supportData, { rejectWithValue }) => {
    try {
      const response = await supportService.createSupport(supportData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSupport = createAsyncThunk(
  "supports/update",
  async ({ supportId, supportData }, { rejectWithValue }) => {
    try {
      const response = await supportService.updateSupport(
        supportId,
        supportData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSupport = createAsyncThunk(
  "supports/delete",
  async (supportId, { rejectWithValue }) => {
    try {
      await supportService.deleteSupport(supportId);
      return supportId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  supports: [],
  currentSupport: null,
  loading: false,
  error: null,
};

const supportSlice = createSlice({
  name: "supports",
  initialState,
  reducers: {
    clearCurrentSupport: (state) => {
      state.currentSupport = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all supports
      .addCase(fetchSupports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupports.fulfilled, (state, action) => {
        state.loading = false;
        state.supports = action.payload;
      })
      .addCase(fetchSupports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch support by ID
      .addCase(fetchSupportById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupportById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSupport = action.payload;
      })
      .addCase(fetchSupportById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create support
      .addCase(createSupport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupport.fulfilled, (state, action) => {
        state.loading = false;
        state.supports.push(action.payload);
      })
      .addCase(createSupport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update support
      .addCase(updateSupport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSupport.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.supports.findIndex(
          (s) => s.id === action.payload.id
        );
        if (index !== -1) {
          state.supports[index] = action.payload;
        }
        if (state.currentSupport?.id === action.payload.id) {
          state.currentSupport = action.payload;
        }
      })
      .addCase(updateSupport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete support
      .addCase(deleteSupport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSupport.fulfilled, (state, action) => {
        state.loading = false;
        state.supports = state.supports.filter((s) => s.id !== action.payload);
        if (state.currentSupport?.id === action.payload) {
          state.currentSupport = null;
        }
      })
      .addCase(deleteSupport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentSupport, clearError } = supportSlice.actions;
export default supportSlice.reducer;
